import { MouseEvent, useEffect, useId, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";

const REDIRECT_SECONDS = 5;

const LinkField = ({
  url,
  label = "Open",
  fallback = "-",
  stopPropagation = false,
  tooltip,
  redirectNotice,
}: {
  url?: string;
  label?: string;
  fallback?: string;
  stopPropagation?: boolean;
  tooltip?: {
    title: string;
    placement?: TooltipProps["placement"];
    arrow?: boolean;
  };
  // Shown in a dialog with a REDIRECT_SECONDS countdown before the link opens.
  redirectNotice?: { title: string; text: string };
}) => {
  // null = dialog closed, 0 = countdown done but the popup was blocked.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const blocked = secondsLeft === 0;
  const noticeId = useId();

  useEffect(() => {
    if (!secondsLeft || !url) return;
    const timer = setTimeout(() => {
      if (secondsLeft > 1) {
        setSecondsLeft(secondsLeft - 1);
        return;
      }
      // The click's user activation has expired by now, so popup blockers may refuse this.
      // Without "noopener" so a blocked popup is detectable (null).
      const tab = window.open(url, "_blank");
      if (tab) tab.opener = null;
      setSecondsLeft(tab ? null : 0);
    }, 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, url]);

  if (!url) return <>{fallback}</>;

  const handleClick = (e: MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    if (!redirectNotice) return;
    e.preventDefault();
    setSecondsLeft(REDIRECT_SECONDS);
  };

  const link = (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
    >
      {label}
    </Link>
  );

  return (
    <>
      {tooltip ? (
        <Tooltip
          title={tooltip.title}
          placement={tooltip.placement ?? "top"}
          arrow={tooltip.arrow ?? true}
        >
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {redirectNotice && (
        <Dialog
          open={secondsLeft !== null}
          onClose={() => setSecondsLeft(null)}
          aria-labelledby={`${noticeId}-title`}
          aria-describedby={`${noticeId}-text`}
          maxWidth="sm"
        >
          <DialogTitle
            id={`${noticeId}-title`}
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            {redirectNotice.title}
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              id={`${noticeId}-text`}
              sx={{ color: "black", textAlign: "center" }}
            >
              {blocked
                ? "Your browser blocked the new tab. Use the button below to open the record."
                : redirectNotice.text}
            </DialogContentText>
          </DialogContent>
          {/* Equal side columns keep the buttons centred with the countdown on the left. */}
          <DialogActions
            disableSpacing
            sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                pl: 1,
                color: "primary.main",
              }}
            >
              {!blocked && (
                <>
                  <CircularProgress
                    variant="determinate"
                    value={((secondsLeft ?? 0) / REDIRECT_SECONDS) * 100}
                    size={14}
                    thickness={6}
                    color="inherit"
                  />
                  <Typography variant="caption">
                    Opening in {secondsLeft} s
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => setSecondsLeft(null)}>Cancel</Button>
              <Button
                variant="contained"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setSecondsLeft(null)}
              >
                Open now
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default LinkField;
